import { inject } from "@angular/core";
import { createEntityAdapter, EntityState } from "@ngrx/entity";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import {
	createActionGroup,
	createFeature,
	createReducer,
	createSelector,
	emptyProps,
	on,
	props,
	Store,
} from "@ngrx/store";
import { catchError, map, of, switchMap, withLatestFrom } from "rxjs";

import { WebviewerDocument } from "../models/webviewer-tms-api.model";
import { WebviewerModificationService } from "../services/webviewer-modification.lib.service";

export const WEBVIEWER_MODIFICATION_FEATURE_KEY = "webviewerModification";

export interface WebviewerModificationState extends EntityState<WebviewerDocument> {
	loading: boolean;
	loaded: boolean;
	error: string | null;
}

const webviewerModificationAdapter = createEntityAdapter<WebviewerDocument>({
	selectId: (document) => document._id,
});

const initialState: WebviewerModificationState = webviewerModificationAdapter.getInitialState({
	loading: false,
	loaded: false,
	error: null,
});

/**
 * Actions for the webviewer modification entity store.
 *
 * - `Load Documents` — fetch all modifications for a customer (by WMS-ID).
 * - `Load Documents By Ids` — fetch specific modifications by ID, using the store as a cache.
 * - `Load Documents Success / Failure` — internal completion actions for the above.
 * - `Upsert Document(s)` — insert or update one / many documents in the store.
 * - `Remove Document` — remove a single document by ID.
 * - `Clear Store` — reset the store to its initial empty state.
 */
export const WebviewerModificationActions = createActionGroup({
	source: "Webviewer Modification",
	events: {
		"Load Documents": props<{ payload: { customerWmsId?: string } }>(),
		"Load Documents Success": props<{ payload: { documents: WebviewerDocument[] } }>(),
		"Load Documents Failure": props<{ payload: { error: string } }>(),
		"Load Documents By Ids": props<{ payload: { ids: string[] } }>(),
		"Upsert Document": props<{ payload: { document: WebviewerDocument } }>(),
		"Upsert Documents": props<{ payload: { documents: WebviewerDocument[] } }>(),
		"Remove Document": props<{ payload: { id: string } }>(),
		"Clear Store": emptyProps(),
	},
});

const webviewerModificationReducer = createReducer(
	initialState,
	on(WebviewerModificationActions.loadDocuments, (state) => ({
		...state,
		loading: true,
		loaded: false,
		error: null,
	})),
	on(WebviewerModificationActions.loadDocumentsSuccess, (state, { payload: { documents } }) =>
		webviewerModificationAdapter.setAll(documents, {
			...state,
			loading: false,
			loaded: true,
			error: null,
		})
	),
	on(WebviewerModificationActions.loadDocumentsFailure, (state, { payload: { error } }) => ({
		...state,
		loading: false,
		loaded: false,
		error: error,
	})),
	on(WebviewerModificationActions.loadDocumentsByIds, (state) => ({
		...state,
		loading: true,
		loaded: false,
		error: null,
	})),
	on(WebviewerModificationActions.upsertDocument, (state, { payload: { document } }) =>
		webviewerModificationAdapter.upsertOne(document, state)
	),
	on(WebviewerModificationActions.upsertDocuments, (state, { payload: { documents } }) =>
		webviewerModificationAdapter.upsertMany(documents, state)
	),
	on(WebviewerModificationActions.removeDocument, (state, { payload: { id } }) =>
		webviewerModificationAdapter.removeOne(id, state)
	),
	on(WebviewerModificationActions.clearStore, () => initialState)
);

export const webviewerModificationFeature = createFeature({
	name: WEBVIEWER_MODIFICATION_FEATURE_KEY,
	reducer: webviewerModificationReducer,
	extraSelectors: ({ selectWebviewerModificationState }) => {
		const entitySelectors = webviewerModificationAdapter.getSelectors(selectWebviewerModificationState);

		return {
			selectWebviewerModificationIds: entitySelectors.selectIds,
			selectWebviewerModificationEntities: entitySelectors.selectEntities,
			selectAllWebviewerModificationDocuments: entitySelectors.selectAll,
			selectWebviewerModificationTotal: entitySelectors.selectTotal,
		};
	},
});

/** Select a single webviewer modification document by its `_id`, or `null` if absent. */
export const selectWebviewerModificationDocumentById = (id: string) =>
	createSelector(
		webviewerModificationFeature.selectWebviewerModificationEntities,
		(entities) => entities[id] ?? null
	);

/** Select multiple webviewer modification documents by their `_id`s (absent ids are omitted). */
export const selectWebviewerModificationDocumentsByIds = (ids: string[]) =>
	createSelector(webviewerModificationFeature.selectWebviewerModificationEntities, (entities) =>
		ids.map((id) => entities[id] ?? null).filter((document) => document !== null)
	);

/** Fetches all modification documents for a customer when `Load Documents` is dispatched. */
export const loadWebviewerModificationDocumentsEffect = createEffect(
	(actions$ = inject(Actions), webviewerModificationService = inject(WebviewerModificationService)) =>
		actions$.pipe(
			ofType(WebviewerModificationActions.loadDocuments),
			switchMap(({ payload }) =>
				webviewerModificationService.findAll(payload.customerWmsId).pipe(
					map((documents) =>
						WebviewerModificationActions.loadDocumentsSuccess({ payload: { documents } })
					),
					catchError((error: unknown) =>
						of(
							WebviewerModificationActions.loadDocumentsFailure({
								payload: { error: extractErrorMessage(error) },
							})
						)
					)
				)
			)
		),
	{ functional: true }
);

/**
 * Fetches modification documents by ID when `Load Documents By Ids` is dispatched.
 * Documents already present in the store are served from cache; only missing IDs
 * trigger an API call.
 */
export const loadWebviewerModificationDocumentsByIdsEffect = createEffect(
	(
		actions$ = inject(Actions),
		webviewerModificationService = inject(WebviewerModificationService),
		store = inject(Store)
	) =>
		actions$.pipe(
			ofType(WebviewerModificationActions.loadDocumentsByIds),
			withLatestFrom(store.select(webviewerModificationFeature.selectWebviewerModificationEntities)),
			switchMap(([{ payload }, entities]) => {
				const cachedDocuments = payload.ids
					.map((id) => entities[id])
					.filter((document) => !!document);
				const missingIds = payload.ids.filter((id) => entities[id] === undefined);

				// Always resolve request with success/failure action.
				// Only hit API when at least one requested id is missing in cache.
				if (missingIds.length === 0) {
					return of(
						WebviewerModificationActions.loadDocumentsSuccess({
							payload: { documents: cachedDocuments },
						})
					);
				}

				return webviewerModificationService.findMany(missingIds).pipe(
					map((fetchedDocuments) => {
						const documents = Array.from(
							new Map(
								[...cachedDocuments, ...fetchedDocuments]
									.filter((document) => !!document)
									.map((document) => [document._id, document])
							).values()
						);
						documents.sort((a, b) => a._id.localeCompare(b._id));
						return WebviewerModificationActions.loadDocumentsSuccess({
							payload: { documents },
						});
					}),
					catchError((error: unknown) =>
						of(
							WebviewerModificationActions.loadDocumentsFailure({
								payload: { error: extractErrorMessage(error) },
							})
						)
					)
				);
			})
		),
	{ functional: true }
);

/** All functional effects for the webviewer modification store – register via `provideEffects`. */
export const webviewerModificationEffects = {
	loadWebviewerModificationDocumentsEffect,
	loadWebviewerModificationDocumentsByIdsEffect,
};

function extractErrorMessage(error: unknown): string {
	if (typeof error === "string") {
		return error;
	}
	if (error instanceof Error) {
		return error.message;
	}
	return "Failed to load webviewer modification documents";
}
